import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Activity, AlertTriangle, ChevronRight, RefreshCw, CheckCircle2,
} from 'lucide-react';
import logger from '@/lib/logger';

const SEVERITY_STYLES = {
  high: 'bg-red-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-400 text-white',
};

const SEVERITY_DOT = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-slate-400',
};

function scoreColor(score) {
  if (score >= 85) return 'text-emerald-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

function DrillDown({ open, onOpenChange, check }) {
  if (!check) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        data-testid="hr-data-health-drilldown"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${SEVERITY_DOT[check.severity]}`}
            />
            {check.label}
          </DialogTitle>
          <DialogDescription>
            {check.fix_hint}{' '}
            <span className="font-medium">
              {check.count} {check.count === 1 ? 'employee' : 'employees'}
            </span>{' '}
            affected.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          {check.items.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              No records — this check is clean.
            </div>
          ) : (
            <div className="border rounded-lg divide-y">
              {check.items.map((it, idx) => (
                <div
                  key={`${it.id || it.employee_id}-${idx}`}
                  className="px-4 py-3 flex items-center justify-between hover:bg-muted/40"
                  data-testid={`data-health-row-${check.key}-${idx}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {it.full_name || '—'}{' '}
                      <span className="text-xs text-muted-foreground">
                        ({it.employee_id})
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {it.department || '—'}
                      {it.designation ? ` · ${it.designation}` : ''}
                      {it.employment_status ? ` · ${it.employment_status}` : ''}
                    </div>
                  </div>
                  <div className="text-right text-xs shrink-0 pl-3">
                    {typeof it.days_remaining === 'number' && (
                      <Badge
                        variant={it.days_remaining < 0 ? 'destructive' : 'outline'}
                      >
                        {it.days_remaining < 0
                          ? `Expired ${Math.abs(it.days_remaining)}d ago`
                          : `${it.days_remaining}d left`}
                      </Badge>
                    )}
                    {it.expiry_date && (
                      <div className="mt-1 text-muted-foreground">
                        {it.expiry_date}
                      </div>
                    )}
                    {it.suggested_lwd && (
                      <div className="text-muted-foreground">
                        Suggest LWD: {it.suggested_lwd}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function HRDataHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCheck, setActiveCheck] = useState(null);
  const [open, setOpen] = useState(false);

  const fetch = async () => {
    try {
      setLoading(true);
      const r = await api.get('/hr/data-health');
      setData(r.data);
    } catch (e) {
      logger.error('Data Health fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openCheck = (check) => {
    setActiveCheck(check);
    setOpen(true);
  };

  if (loading && !data) {
    return (
      <Card data-testid="hr-data-health-widget-loading">
        <CardContent className="py-10 flex items-center justify-center text-muted-foreground">
          <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Scanning employee records…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const allClean = data.total_anomalies === 0;

  return (
    <Card data-testid="hr-data-health-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-indigo-500" />
            HR Data Health
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetch}
            data-testid="hr-data-health-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-6 mb-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Score
            </p>
            <p
              className={`text-4xl font-bold ${scoreColor(data.health_score)}`}
              data-testid="hr-data-health-score"
            >
              {data.health_score}
              <span className="text-base text-muted-foreground ml-1">/100</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Scanned
            </p>
            <p className="text-2xl font-semibold">{data.scanned_employees}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Anomalies
            </p>
            <p className="text-2xl font-semibold">
              {data.total_anomalies}
              {data.high_severity_anomalies > 0 && (
                <span className="text-sm text-red-600 ml-2">
                  ({data.high_severity_anomalies} high)
                </span>
              )}
            </p>
          </div>
        </div>

        {allClean ? (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm font-medium">
              All data-quality checks are passing. Nice work!
            </span>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {data.checks
              .filter((c) => c.count > 0)
              .sort((a, b) => {
                const order = { high: 0, medium: 1, low: 2 };
                return (order[a.severity] - order[b.severity]) || (b.count - a.count);
              })
              .map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => openCheck(c)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 text-left transition-colors"
                  data-testid={`hr-data-health-check-${c.key}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`h-2 w-2 rounded-full ${SEVERITY_DOT[c.severity]}`}
                    />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.label}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.fix_hint}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={SEVERITY_STYLES[c.severity]}>
                      {c.count}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            {data.checks.every((c) => c.count === 0) && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                All checks clean.
              </div>
            )}
          </div>
        )}

        {data.total_anomalies > 0 && data.high_severity_anomalies === 0 && (
          <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            No high-severity issues. Consider addressing medium/low items when time permits.
          </p>
        )}
      </CardContent>

      <DrillDown open={open} onOpenChange={setOpen} check={activeCheck} />
    </Card>
  );
}

export default HRDataHealthWidget;
